import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, apiBlob } from './hrApi';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState(null);

  const loadAvatar = useCallback(async (hasPhoto) => {
    if (hasPhoto) {
      try { setAvatarUrl(await apiBlob('/photo')); } catch {}
    }
  }, []);

  const enterApp = useCallback((emp) => {
    setUser(emp);
    loadAvatar(emp.has_photo);
  }, [loadAvatar]);

  // On mount: call /api/kiosk/me with BHEL token to get HR user payload
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }

    api('/me')
      .then(enterApp)
      .catch(() => {
        // /me failed — try SSO to initialise the session
        return fetch('/api/kiosk/sso', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })
          .then(r => r.json())
          .then(d => { if (d.employee) enterApp(d.employee); })
          .catch(() => {});
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const ssoLogin = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    setLoading(true);
    try {
      const d = await api('/me');
      enterApp(d);
    } catch {
      try {
        const r = await fetch('/api/kiosk/sso', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        const data = await r.json();
        if (data.employee) enterApp(data.employee);
      } catch {}
    } finally {
      setLoading(false);
    }
  }, [enterApp]);

  const logout = () => { setUser(null); setAvatarUrl(null); };

  const has = (feat) => user && (user.role === 'ADMIN' || user.features?.[feat]);

  return (
    <AuthCtx.Provider value={{ user, loading, avatarUrl, logout, has, ssoLogin, enterApp }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() { return useContext(AuthCtx); }
